import React from 'react';
import { publicContentUrl } from '../config/ServiceConfig';
import Skeleton from '@mui/material/Skeleton';

export type ImageSize = 'thumb' | 'medium' | 'large' | 'original';

export interface ResponsiveImageProps {
    kandiId: string;
    photoIndex?: number;
    alt: string;
    className?: string;
    preferredSize?: ImageSize;
    fallbackToOriginal?: boolean;
    showSkeleton?: boolean;
}

/**
 * Get the URL for a specific image size with multi-photo support
 */
export const getImageUrl = (kandiId: string, photoIndex: number = 0, size: ImageSize = 'original'): string => {
    if (size === 'original') {
        // Original photo: public/kandis/{kandiId}/{photoIndex}
        return `${publicContentUrl}kandis/${kandiId}/${photoIndex}`;
    } else {
        // Resized photo: public/kandis/{kandiId}/{photoIndex}/{size}
        return `${publicContentUrl}kandis/${kandiId}/${photoIndex}/${size}`;
    }
};

/**
 * Get the main photo URL (photoIndex 0) for backward compatibility
 */
export const getMainPhotoUrl = (kandiId: string, size: ImageSize = 'original'): string => {
    return getImageUrl(kandiId, 0, size);
};

/**
 * Get the appropriate image size based on container width
 */
export const getResponsiveImageSize = (containerWidth: number): ImageSize => {
    if (containerWidth <= 200) {
        return 'thumb';
    } else if (containerWidth <= 600) {
        return 'medium';
    } else {
        return 'large';
    }
};

/**
 * Get multiple image URLs for responsive loading with fallbacks
 * Simplified fallback: preferred size → original (for faster resolution)
 */
export const getResponsiveImageUrls = (
    kandiId: string, 
    photoIndex: number = 0, 
    preferredSize: ImageSize = 'medium'
): string[] => {
    const urls: string[] = [];
    
    // Add preferred size first
    urls.push(getImageUrl(kandiId, photoIndex, preferredSize));
    
    // Add original as fallback (always exists)
    if (preferredSize !== 'original') {
        urls.push(getImageUrl(kandiId, photoIndex, 'original'));
    }
    
    return urls;
};

/**
 * Hook to handle image loading with fallbacks for multi-photo support
 */
export const useImageWithFallback = (
    kandiId: string, 
    photoIndex: number = 0, 
    preferredSize: ImageSize = 'medium'
) => {
    const [currentImageUrl, setCurrentImageUrl] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [hasError, setHasError] = React.useState<boolean>(false);
    
    React.useEffect(() => {
        const urls = getResponsiveImageUrls(kandiId, photoIndex, preferredSize);
        let currentIndex = 0;
        
        const tryLoadImage = () => {
            if (currentIndex >= urls.length) {
                setHasError(true);
                setIsLoading(false);
                return;
            }
            
            const img = new Image();
            const currentUrl = urls[currentIndex];
            
            img.onload = () => {
                setCurrentImageUrl(currentUrl);
                setIsLoading(false);
                setHasError(false);
            };
            
            img.onerror = () => {
                currentIndex++;
                tryLoadImage();
            };
            
            img.src = currentUrl;
        };
        
        setIsLoading(true);
        setHasError(false);
        tryLoadImage();
        
    }, [kandiId, photoIndex, preferredSize]);
    
    return { imageUrl: currentImageUrl, isLoading, hasError };
};

/**
 * Get URLs for all photos of a Kandi (for gallery view)
 */
export const getKandiPhotoUrls = (
    kandiId: string, 
    photoCount: number, 
    size: ImageSize = 'medium'
): string[] => {
    const urls: string[] = [];
    
    for (let i = 0; i < photoCount; i++) {
        urls.push(getImageUrl(kandiId, i, size));
    }
    
    return urls;
};

/**
 * Responsive Image Component with multi-photo support, optional skeleton loading, and smooth fade transition
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
    kandiId,
    photoIndex = 0,
    alt,
    className = '',
    preferredSize = 'medium',
    fallbackToOriginal = true,
    showSkeleton = true
}) => {
    const { imageUrl, isLoading, hasError } = useImageWithFallback(kandiId, photoIndex, preferredSize);
    const [imageLoaded, setImageLoaded] = React.useState<boolean>(false);
    
    // Reset imageLoaded state when imageUrl changes
    React.useEffect(() => {
        setImageLoaded(false);
    }, [imageUrl]);
    
    // Determine skeleton dimensions based on preferred size
    const getSkeletonDimensions = () => {
        switch (preferredSize) {
            case 'thumb':
                return { width: 150, height: 150 };
            case 'medium':
                return { width: 300, height: 300 };
            case 'large':
                return { 
                    width: 'min(75vw, 600px)',  // Responsive width matching kandiPhoto maxWidth
                    height: 'min(60vh, 600px)'  // Responsive height matching kandiPhoto maxHeight
                };
            default:
                return { width: 300, height: 300 };
        }
    };
    
    const { width, height } = getSkeletonDimensions();
    
    // Show skeleton while loading (if enabled)
    if (isLoading && showSkeleton) {
        return (
            <Skeleton 
                variant="rectangular" 
                width={width}
                height={height}
                className={className}
                animation="pulse"
            />
        );
    }
    
    // Show skeleton for errors (if enabled)
    if (hasError && showSkeleton) {
        return (
            <Skeleton 
                variant="rectangular" 
                width={width}
                height={height}
                className={className}
                animation={false}
                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
            />
        );
    }
    
    // If skeleton is disabled, show nothing while loading or on error
    if (isLoading || hasError) {
        return null;
    }
    
    // Always render the image (but control visibility with opacity) so onLoad can fire
    return (
        <img 
            src={imageUrl} 
            alt={alt} 
            className={className}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            style={{
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
            }}
        />
    );
};
